import type { ReactNode } from 'react';
import { Button, Drawer, DrawerFooter, DrawerHeader, DrawerTitle } from '@herit-fe/heritage-ui';
import { ListRestart } from 'lucide-react';
import { UI_TEXT } from '@/shared/languages';
import { DrawerContent } from './drawer-content';
import { DrawerCloseButton } from './drawer-close-button';
import { ScrollArea } from './scroll-area';
import { useIsCompact } from './use-is-compact';

export interface FilterShellProps {
  /** 시트 열림 상태(제어형) — 트리거는 밖(Toolbar.filter)에서 소유. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 필터 컨트롤 본체 — 무상태(상위 draft 바인딩). `ScrollArea` 로 감싸 렌더. */
  children: ReactNode;
  /**
   * **적용 모드** — 주어지면 staged: 푸터 [조회]로 명시 커밋(`false` 반환 시 시트 유지). 생략하면 live: 푸터 [닫기]만
   * (컨트롤 변경 즉시 적용 전제 — 연속 입력은 feature 디바운스).
   */
  onApply?: () => boolean | void;
  /** [조회] 활성(기본 true). */
  canApply?: boolean;
  /** [초기화](선택) — 푸터. 변경 있을 때만(canReset). */
  onReset?: () => void;
  /** 초기화 노출 조건(기본 true — 화면이 isDirty 등 주입). */
  canReset?: boolean;
  /** 시트 헤더 타이틀(기본 조회조건). */
  title?: string;
}

/**
 * 조회 필터 쉘(제어형) — 트리거·칩과 분리된 Drawer 본체. 트리거는 `Toolbar.filter`(a·c·d)가, 칩은
 * `FilterChips`(c 또는 카드 안)가 소유한다(b→a 통일). 반응형 `Drawer`(vaul, non-pc bottom·드래그 핸들 / pc right),
 * 본문만 `ScrollArea` 스크롤(헤더·푸터 고정). 이름은 tech-neutral(내부 표면이 Drawer 라 `*Sheet` 아님).
 */
export function FilterShell({
  open,
  onOpenChange,
  children,
  onApply,
  canApply = true,
  onReset,
  canReset = true,
  title,
}: FilterShellProps) {
  const isCompact = useIsCompact();
  const direction = isCompact ? 'bottom' : 'right';

  const handleApply = () => {
    if (!onApply) return;
    const result = onApply();
    if (result !== false) onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction={direction}>
      <DrawerContent
        aria-describedby={undefined}
        className={`gap-0 overflow-hidden ${direction === 'bottom' ? 'max-h-[85dvh]!' : 'sm:max-w-md'}`}
      >
        <DrawerCloseButton />
        <DrawerHeader className="shrink-0 border-b border-border">
          <DrawerTitle className="text-base font-semibold">
            {title ?? UI_TEXT.FILTER_TITLE}
          </DrawerTitle>
        </DrawerHeader>
        <ScrollArea className="flex flex-col flex-1 min-h-0">
          <div className="p-4">{children}</div>
        </ScrollArea>
        <DrawerFooter className="shrink-0 flex-row gap-2 border-t border-border">
          {onReset && canReset ? (
            <Button variant="ghost" size="lg" onClick={onReset} className="gap-2">
              {UI_TEXT.FILTER_RESET}
              <ListRestart className="size-4" />
            </Button>
          ) : null}
          {onApply ? (
            <Button size="lg" className="flex-1" onClick={handleApply} disabled={!canApply}>
              {UI_TEXT.FILTER_APPLY}
            </Button>
          ) : (
            <Button size="lg" className="flex-1" onClick={() => onOpenChange(false)}>
              {UI_TEXT.COMMON_CLOSE}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
